<template>
  <layout-content-renderer-left
    class="chat-application"
    :style="{ height: alturaPagina }"
  >
    <template #breadcrumb>
      <div />
    </template>

    <portal to="content-renderer-sidebar-left">
      <lista-conversas
        :conversas="conversas"
        :carregando="carregandoConversas"
        :conversa-ativa-id="conversaAtivaId"
        :aba="filtroAba"
        @trocar-aba="trocarAba"
        @selecionar="selecionar"
      />
    </portal>

    <div class="chat-app-window">
      <janela-mensagens
        v-if="conversaAtivaId && conversaAtiva"
        :conversa="conversaAtiva"
        :mensagens="mensagensDaConversaAtiva"
        :carregando="carregandoMensagens"
        :departamentos="departamentos"
        @assumir="chamarAcao('assumir', $event, 'Atendimento assumido')"
        @transferir="
          chamarAcao('transferir', $event, 'Atendimento transferido')
        "
        @atribuir="
          chamarAcao('atribuir', $event, 'Atendimento transferido para o operador')
        "
        @encerrar="chamarAcao('encerrar', $event, 'Atendimento encerrado')"
        @reabrir="chamarAcao('reabrir', $event, 'Atendimento reaberto')"
        @enviar="enviarTexto"
        @enviar-arquivo="enviarArquivo"
      />
      <div v-else class="start-chat-area">
        <div class="mb-1 start-chat-icon">
          <feather-icon icon="MessageSquareIcon" size="56" />
        </div>
        <h4 class="start-chat-text">Selecione uma conversa na lista ao lado</h4>
      </div>
    </div>
  </layout-content-renderer-left>
</template>

<script>
import { mapState, mapGetters, mapActions, mapMutations } from "vuex";
import LayoutContentRendererLeft from "@core/layouts/components/layout-content-renderer/LayoutContentRendererLeft.vue";
import FeatherIcon from "@core/components/feather-icon/FeatherIcon.vue";
import ListaConversas from "./components/ListaConversas.vue";
import JanelaMensagens from "./components/JanelaMensagens.vue";
import { pedirPermissaoNotificacao, tocarCampainha, notificarSistemaOperacional } from "@/utils/notificacoes";

export default {
  components: {
    LayoutContentRendererLeft,
    FeatherIcon,
    ListaConversas,
    JanelaMensagens,
  },
  data() {
    return {
      departamentos: [],
      // O mixin generico de altura do Vuexy (_content.scss) nunca foi
      // exercitado neste projeto - todas as outras telas sao tabela com
      // scroll de pagina normal. Em vez de depurar a cadeia de classes
      // navbar-sticky/footer-static as cegas, medimos e fixamos a altura
      // na mao: robusto independente do que o mecanismo generico faca.
      alturaPagina: "calc(100vh - 12rem)",
    };
  },
  computed: {
    ...mapState("atendimento", [
      "conversas",
      "carregandoConversas",
      "conversaAtivaId",
      "conversaAtiva",
      "carregandoMensagens",
      "filtroAba",
      "ultimaNotificacao",
    ]),
    ...mapGetters("atendimento", ["mensagensDaConversaAtiva"]),
  },
  watch: {
    ultimaNotificacao(dados) {
      if (!dados) return;
      // Ja esta olhando bem pra essa conversa, com a aba em foco - nao
      // precisa de toast nem campainha, so ia incomodar.
      if (dados.atendimentoId === this.conversaAtivaId && !document.hidden) return;

      const rotuloTipo = { imagem: "[imagem]", audio: "[áudio]", video: "[vídeo]", documento: "[documento]", sticker: "[figurinha]" };
      const previa = dados.texto || rotuloTipo[dados.tipo] || "Nova mensagem";
      const titulo = "Nova mensagem" + (dados.contatoNome ? " de " + dados.contatoNome : "");

      tocarCampainha();
      this.$message(titulo, previa, "info");
      notificarSistemaOperacional(titulo, previa);
    },
  },
  created() {
    this.carregarConversas();
    this.carregarDepartamentos().then(() => {
      this.inscreverFilas();
      this.inscreverNotificacoesGlobais({
        usuarioId: this.$store.state.user.id,
        departamentoIds: this.idsDepartamentosParaAssinar(),
      });
    });
    pedirPermissaoNotificacao();
  },
  mounted() {
    this.ajustarAltura();
    window.addEventListener("resize", this.ajustarAltura);
  },
  beforeDestroy() {
    if (this.conversaAtivaId) {
      this.sairCanalConversa(this.conversaAtivaId);
    }
    this.sairFilas();
    this.sairNotificacoesGlobais({
      usuarioId: this.$store.state.user.id,
      departamentoIds: this.idsDepartamentosParaAssinar(),
    });
    window.removeEventListener("resize", this.ajustarAltura);
  },
  methods: {
    ...mapActions("atendimento", [
      "carregarConversas",
      "inscreverNotificacoesGlobais",
      "sairNotificacoesGlobais",
      "selecionarConversa",
      "sairCanalConversa",
      "inscreverCanaisDepartamento",
      "sairCanaisDepartamento",
    ]),
    ...mapMutations("atendimento", ["SET_FILTRO_ABA"]),
    carregarDepartamentos() {
      return window.axios
        .get(this.$store.state.api + "departamentos", {
          params: { paginated: false },
        })
        .then((resposta) => {
          this.departamentos = resposta.data.data;
        })
        .catch(() => {});
    },
    trocarAba(aba) {
      this.SET_FILTRO_ABA(aba);
      this.carregarConversas();
    },
    selecionar(id) {
      if (this.conversaAtivaId && this.conversaAtivaId !== id) {
        this.sairCanalConversa(this.conversaAtivaId);
      }
      this.selecionarConversa(id).catch((error) => this.$error(error));
    },
    enviarTexto(texto) {
      const clientId =
        "c-" + Date.now() + "-" + Math.random().toString(36).slice(2, 11);
      this.$store
        .dispatch("atendimento/enviarTexto", {
          atendimentoId: this.conversaAtivaId,
          texto,
          clientId,
        })
        .catch((error) => this.$error(error));
    },
    enviarArquivo({ arquivo, legenda }) {
      this.$store
        .dispatch("atendimento/enviarMidia", {
          atendimentoId: this.conversaAtivaId,
          arquivo,
          legenda,
        })
        .catch((error) => this.$error(error));
    },
    chamarAcao(nomeAcao, payload, mensagemSucesso) {
      this.$store
        .dispatch("atendimento/" + nomeAcao, payload)
        .then(() => this.$message(null, mensagemSucesso, "success"))
        .catch((error) => this.$error(error));
    },
    ajustarAltura() {
      this.$nextTick(() => {
        if (!this.$el || !this.$el.getBoundingClientRect) return;
        const topo = this.$el.getBoundingClientRect().top;
        // O footer do layout (rodape "IT4D...") fica FORA do .app-content,
        // em fluxo normal logo abaixo - sem descontar a altura dele aqui,
        // o container do chat + footer juntos passam de 100vh e sobra um
        // scroll de pagina inteiro so por causa dessa faixa.
        const footer = document.querySelector(".footer");
        const alturaFooter = footer ? footer.getBoundingClientRect().height : 0;
        const altura = window.innerHeight - topo - alturaFooter;
        this.alturaPagina = Math.max(altura, 400) + "px";
      });
    },
    // Admin/supervisor enxergam a fila de qualquer departamento (a policy
    // do backend ja libera isso) - assinamos todos. Operador so assina os
    // proprios (vindos do /me), senao a lista de "Fila" so atualiza
    // sozinha quando ele proprio mexe em algo.
    idsDepartamentosParaAssinar() {
      const perfil = this.$store.state.user.perfil;
      if (perfil === "admin" || perfil === "supervisor") {
        return this.departamentos.map((d) => d.id);
      }
      return (this.$store.state.user.departamentos || []).map((d) => d.id);
    },
    inscreverFilas() {
      const ids = this.idsDepartamentosParaAssinar();
      if (ids.length) this.inscreverCanaisDepartamento(ids);
    },
    sairFilas() {
      const ids = this.idsDepartamentosParaAssinar();
      if (ids.length) this.sairCanaisDepartamento(ids);
    },
  },
};
</script>

<style lang="scss">
@import "@core/scss/base/pages/app-chat.scss";
@import "@core/scss/base/pages/app-chat-list.scss";

// Os parciais acima nunca declaram overflow nos dois paineis que rolam -
// no Vuexy original isso vinha de uma lib de scrollbar (vue-perfect-
// scrollbar, nao instalada aqui) embutida na view de chat removida deste
// projeto. Sem isso, o conteudo so cresce e empurra a pagina inteira.
// .app-content aplica padding lateral/inferior generico (pensado pra
// paginas de tabela/cartao) que so desperdica espaco numa tela de chat -
// mantemos so o padding de cima (necessario pra nao ficar embaixo da
// navbar fixa). Precisa de !important: a regra generica do tema tem mais
// especificidade (encadeia .navbar-sticky.footer-static .app-content).
.app-content.chat-application {
  padding-left: 0 !important;
  padding-right: 0 !important;
  padding-bottom: 0 !important;
}

.chat-application {
  height: 100%;
  overflow: hidden;

  .content-area-wrapper {
    height: 100%;
    overflow: hidden;
    border-width: 0 !important;
    border-radius: 0 !important;
  }

  // A cadeia inteira precisa de height:100% explicito - o generico do tema
  // so garante isso em .content-wrapper/.content-body (via _content.scss);
  // .content-right fica de fora e dependeria de flex-stretch, que nem
  // sempre resolve como esperado com filhos block dentro. Reforcar todo
  // mundo elimina a ambiguidade.
  .content-right,
  .content-wrapper,
  .content-body,
  .sidebar-content,
  .chat-app-window {
    height: 97.8%;
  }

  .chat-user-list-wrapper {
    overflow-y: auto;
  }

  // .active-chat (cabecalho/mensagens/compositor) tem seu proprio flexbox
  // scoped em JanelaMensagens.vue, co-localizado com o template que afeta.

  .start-chat-area {
    height: 100%;
  }
}
</style>
